import { useEffect, useState, useCallback, useRef } from 'react';
import { amazonSupportAPI } from '@/lib/api';
import { useProfile } from '@/lib/profile-context';
import { useSeller } from '@/components/layout/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, ShieldAlert, Inbox, Bell, RefreshCw, Play, Terminal } from 'lucide-react';
import { toast } from 'sonner';

function extractApiList(response) {
  const body = response?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
}

function policyViolationRowKey(row, index) {
  const idPart = row.id != null ? String(row.id) : 'no-id';
  return `pv-${idPart}-${index}-${row.asin ?? ''}-${row.issue_date ?? ''}-${(row.violation_type || '').slice(0, 40)}`;
}

function vocRowKey(row, index) {
  const idPart = row.id != null ? String(row.id) : 'no-id';
  return `voc-${idPart}-${index}-${row.asin ?? ''}`;
}

function performanceNotificationRowKey(row, index) {
  const idPart = row.id != null ? String(row.id) : 'no-id';
  const url = String(row.url || '').slice(0, 160);
  const subj = String(row.subject || row.detail_subject || '').slice(0, 80);
  const d = String(row.date || row.detail_date || '').slice(0, 40);
  return `pn-${idPart}-${index}-${url}-${subj}-${d}`.replace(/\s+/g, '_');
}

function formatValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  const s = String(value);
  return s.trim() === '' ? '—' : s;
}

function DetailFieldList({ orderedKeys, item }) {
  const shown = new Set();
  const rows = [];

  orderedKeys.forEach(({ key, label }) => {
    if (!(key in item)) return;
    shown.add(key);
    rows.push(
      <div key={key} className="grid grid-cols-1 gap-1 sm:grid-cols-[140px_1fr] sm:gap-3 text-sm border-b border-border/40 pb-2 last:border-0">
        <dt className="text-muted-foreground shrink-0">{label}</dt>
        <dd className="text-foreground break-words whitespace-pre-wrap min-w-0">{formatValue(item[key])}</dd>
      </div>
    );
  });

  Object.keys(item)
    .sort()
    .forEach((key) => {
      if (shown.has(key)) return;
      const val = item[key];
      if (val === null || val === undefined || String(val).trim() === '') return;
      rows.push(
        <div key={key} className="grid grid-cols-1 gap-1 sm:grid-cols-[140px_1fr] sm:gap-3 text-sm border-b border-border/40 pb-2 last:border-0">
          <dt className="text-muted-foreground shrink-0">{key}</dt>
          <dd className="text-foreground break-words whitespace-pre-wrap min-w-0">{formatValue(val)}</dd>
        </div>
      );
    });

  return <dl className="space-y-2">{rows}</dl>;
}

export default function SupportDataPage() {
  const { accountId } = useProfile();
  const { syncVersion, dateFrom, dateTo } = useSeller();
  const [loading, setLoading] = useState(true);
  const [policyViolations, setPolicyViolations] = useState([]);
  const [voiceOfCustomer, setVoiceOfCustomer] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [detail, setDetail] = useState({ open: false, type: null, item: null });
  const [scrapeLogs, setScrapeLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const logsPreRef = useRef(null);

  const closeDetail = () => setDetail({ open: false, type: null, item: null });

  const loadData = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    const dateParams = { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined };
    try {
      const [pvRes, vocRes, notifRes] = await Promise.all([
        amazonSupportAPI.getPolicyViolations(accountId, dateParams),
        amazonSupportAPI.getVoiceOfCustomer(accountId, dateParams),
        amazonSupportAPI.getPerformanceNotifications(accountId, dateParams),
      ]);
      setPolicyViolations(extractApiList(pvRes));
      setVoiceOfCustomer(extractApiList(vocRes));
      setNotifications(extractApiList(notifRes));
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load support data';
      toast.error(msg);
      setPolicyViolations([]);
      setVoiceOfCustomer([]);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, dateFrom, dateTo]);

  const loadScrapeLogs = useCallback(async () => {
    if (!accountId) return;
    setLogsLoading(true);
    try {
      const res = await amazonSupportAPI.getScrapeLogs(accountId, 250);
      const content = res?.data?.data?.content || '';
      setScrapeLogs(content);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load scrape logs';
      toast.error(msg);
      setScrapeLogs('');
    } finally {
      setLogsLoading(false);
    }
  }, [accountId]);

  const triggerScrape = async () => {
    if (!accountId) return;
    setTriggering(true);
    try {
      await amazonSupportAPI.triggerScrape(accountId);
      toast.success('Amazon fetch started');
      await loadScrapeLogs();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to start amazon fetch';
      toast.error(msg);
    } finally {
      setTriggering(false);
    }
  };

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      setScrapeLogs('');
      return;
    }
    loadData();
    loadScrapeLogs();
  }, [accountId, syncVersion, loadData, loadScrapeLogs]);

  useEffect(() => {
    if (!logsPreRef.current) return;
    logsPreRef.current.scrollTop = logsPreRef.current.scrollHeight;
  }, [scrapeLogs]);

  const accountHealthRating = policyViolations.find(
    (v) => v.account_health_rating != null && v.account_health_rating !== ''
  )?.account_health_rating;

  const openPolicyDetail = (item) =>
    setDetail({ open: true, type: 'policy', item });
  const openVocDetail = (item) => setDetail({ open: true, type: 'voc', item });
  const openNotifDetail = (item) => setDetail({ open: true, type: 'performance', item });

  if (!accountId) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Seller Central scraped data</h1>
        <p className="text-sm text-muted-foreground">
          Select an account and profile first. Data shown here is what <code className="text-xs">amazon.py</code> saves
          after running <code className="text-xs">php artisan app:fetch-amazon-data</code>.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const policyOrdered = [
    { key: 'reason_message', label: 'Reason message' },
    { key: 'violation_type', label: 'Violation type' },
    { key: 'issue_date', label: 'Issue date' },
    { key: 'asin', label: 'ASIN' },
    { key: 'product_name', label: 'Product name' },
    { key: 'severity', label: 'Severity' },
    { key: 'resolution_status', label: 'Resolution status' },
    { key: 'action_required', label: 'Action required' },
    { key: 'account_health_rating', label: 'Account health rating' },
    { key: 'id', label: 'Record id' },
    { key: 'created_at', label: 'Created at' },
    { key: 'updated_at', label: 'Updated at' },
  ];

  const vocOrdered = [
    { key: 'product_name', label: 'Product name' },
    { key: 'asin', label: 'ASIN' },
    { key: 'sku_condition', label: 'Category / condition' },
    { key: 'merchant', label: 'Merchant' },
    { key: 'ncx_rate', label: 'NCX rate' },
    { key: 'returns', label: 'Returns' },
    { key: 'units_sold', label: 'Units sold' },
    { key: 'star_rating', label: 'Star rating' },
    { key: 'customer_feedback', label: 'Customer feedback' },
    { key: 'listed_date', label: 'Listed date' },
    { key: 'cx_health', label: 'CX health' },
    { key: 'needs_action', label: 'Needs action' },
    { key: 'product_image', label: 'Product image URL' },
    { key: 'id', label: 'Record id' },
    { key: 'created_at', label: 'Created at' },
    { key: 'updated_at', label: 'Updated at' },
  ];

  const notifOrdered = [
    { key: 'subject', label: 'Subject (list)' },
    { key: 'detail_subject', label: 'Subject (detail page)' },
    { key: 'date', label: 'Date (list)' },
    { key: 'detail_date', label: 'Date (detail page)' },
    { key: 'deadline', label: 'Deadline' },
    { key: 'action_required', label: 'Action required' },
    { key: 'url', label: 'URL' },
    { key: 'message', label: 'Message' },
    { key: 'id', label: 'Record id' },
    { key: 'created_at', label: 'Created at' },
    { key: 'updated_at', label: 'Updated at' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Seller Central scraped data</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Policy violations, Voice of Customer, and performance notifications for the selected account (from Seller
            Central scrape). Click a row for full details.
          </p>
          {accountHealthRating != null && (
            <p className="text-xs text-muted-foreground mt-2">
              Latest account health rating (from scrape):{' '}
              <span className="font-medium tabular-nums text-foreground">{accountHealthRating}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="shrink-0 gap-2" onClick={() => loadData()}>
            <RefreshCw className="w-4 h-4" />
            Refresh data
          </Button>
          <Button type="button" variant="default" size="sm" className="shrink-0 gap-2" onClick={triggerScrape} disabled={triggering}>
            {triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Trigger fetch
          </Button>
          <Button type="button" variant="outline" size="sm" className="shrink-0 gap-2" onClick={loadScrapeLogs} disabled={logsLoading}>
            {logsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Terminal className="w-4 h-4" />}
            Refresh logs
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="col-span-1 xl:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-400" />
              <CardTitle className="text-sm font-medium">Policy violations</CardTitle>
            </div>
            <Badge variant="outline" className="text-xs">
              {policyViolations.length} items
            </Badge>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[14rem] overflow-auto">
            {policyViolations.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No rows yet. Run the fetch command after granting support access so the scraper can populate this table.
              </p>
            ) : (
              policyViolations.map((v, idx) => (
                <button
                  type="button"
                  key={policyViolationRowKey(v, idx)}
                  onClick={() => openPolicyDetail(v)}
                  className="w-full text-left border border-border/60 rounded-md p-2.5 space-y-1 bg-card/40 cursor-pointer transition-colors hover:bg-muted/30 hover:border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium line-clamp-1">{v.reason_message || v.violation_type || 'Policy violation'}</span>
                    {v.severity && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                        {v.severity}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{v.issue_date || '—'}</span>
                    <span>{v.asin || '—'}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground line-clamp-3">{v.product_name || ''}</p>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">Status: {v.resolution_status || '—'}</span>
                    <span className="text-xs">
                      {String(v.action_required || '').toLowerCase() === 'resolve' ? (
                        <Badge
                          variant="outline"
                          className="border-amber-500/40 text-amber-400 bg-amber-500/10 text-[10px] px-1.5 py-0"
                        >
                          Action required
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          Info only
                        </Badge>
                      )}
                    </span>
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="col-span-1 xl:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-sky-400" />
              <CardTitle className="text-sm font-medium">Voice of the customer</CardTitle>
            </div>
            <Badge variant="outline" className="text-xs">
              {voiceOfCustomer.length} products
            </Badge>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[14rem] overflow-auto">
            {voiceOfCustomer.length === 0 ? (
              <p className="text-xs text-muted-foreground">No Voice of Customer rows scraped for this account yet.</p>
            ) : (
              voiceOfCustomer.map((p, idx) => (
                <button
                  type="button"
                  key={vocRowKey(p, idx)}
                  onClick={() => openVocDetail(p)}
                  className="w-full text-left border border-border/60 rounded-md p-2.5 flex gap-2 bg-card/40 cursor-pointer transition-colors hover:bg-muted/30 hover:border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {p.product_image ? (
                    <div className="w-10 h-10 rounded-md bg-muted overflow-hidden flex-shrink-0">
                      <img
                        src={p.product_image}
                        alt={p.product_name || 'Product'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center text-[10px] text-muted-foreground flex-shrink-0">
                      —
                    </div>
                  )}
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium line-clamp-1">{p.product_name || 'Product'}</span>
                      {p.cx_health && (
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 py-0 shrink-0 ${
                            ['Poor', 'Very Poor'].includes(p.cx_health)
                              ? 'border-red-500/40 text-red-400 bg-red-500/10'
                              : ''
                          }`}
                        >
                          {p.cx_health}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>{p.asin || '—'}</span>
                      <span>NCX: {p.ncx_rate || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>Returns: {p.returns ?? '—'}</span>
                      <span>Sold: {p.units_sold ?? '—'}</span>
                    </div>
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="col-span-1 xl:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-400" />
              <CardTitle className="text-sm font-medium">Performance notifications</CardTitle>
            </div>
            <Badge variant="outline" className="text-xs">
              {notifications.length} messages
            </Badge>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[14rem] overflow-auto">
            {notifications.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No performance notifications scraped for this account yet.
              </p>
            ) : (
              notifications.map((n, idx) => (
                <button
                  type="button"
                  key={performanceNotificationRowKey(n, idx)}
                  onClick={() => openNotifDetail(n)}
                  className="w-full text-left border border-border/60 rounded-md p-2.5 space-y-1 bg-card/40 cursor-pointer transition-colors hover:bg-muted/30 hover:border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[4.5rem]"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium line-clamp-2 break-words">
                      {n.subject || n.detail_subject || 'Notification'}
                    </span>
                    {String(n.action_required || '').toLowerCase() === 'yes' && (
                      <Badge
                        variant="outline"
                        className="border-amber-500/40 text-amber-400 bg-amber-500/10 text-[10px] px-1.5 py-0 shrink-0"
                      >
                        Action required
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground gap-2">
                    <span>{n.date || n.detail_date || '—'}</span>
                    <span className="truncate">{n.deadline || ''}</span>
                  </div>
                  {n.message && (
                    <p className="text-[11px] text-muted-foreground whitespace-pre-wrap line-clamp-3 break-words">
                      {n.message}
                    </p>
                  )}
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-sm font-medium">amazon.py run logs</CardTitle>
          <Badge variant="outline" className="text-xs">
            Last 250 lines
          </Badge>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading logs...
            </div>
          ) : scrapeLogs ? (
            <pre
              ref={logsPreRef}
              className="bg-muted/30 border border-border/60 rounded-md p-3 text-xs leading-5 whitespace-pre-wrap break-words max-h-[12rem] overflow-auto"
            >
              {scrapeLogs}
            </pre>
          ) : (
            <p className="text-xs text-muted-foreground">
              No scrape logs yet. Click <span className="font-medium">Trigger fetch</span> to start `app:fetch-amazon-data`.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={detail.open} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detail.type === 'policy' && 'Policy violation'}
              {detail.type === 'voc' && 'Voice of the customer'}
              {detail.type === 'performance' && 'Performance notification'}
            </DialogTitle>
            <DialogDescription>
              Full scraped fields for this record. Additional columns from the database appear at the bottom.
            </DialogDescription>
          </DialogHeader>
          {detail.item && detail.type === 'policy' && <DetailFieldList orderedKeys={policyOrdered} item={detail.item} />}
          {detail.item && detail.type === 'voc' && <DetailFieldList orderedKeys={vocOrdered} item={detail.item} />}
          {detail.item && detail.type === 'performance' && (
            <>
              <DetailFieldList orderedKeys={notifOrdered} item={detail.item} />
              {detail.item.url && (
                <p className="text-xs text-muted-foreground pt-2">
                  Open original:{' '}
                  <a
                    href={detail.item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[hsl(188,100%,55%)] underline break-all"
                  >
                    {detail.item.url}
                  </a>
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
